import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Container, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, Link } from 'react-router-dom';
import PhotoCamera from '@mui/icons-material/PhotoCamera';

// Définition du type pour le fichier
interface FileWithPreview extends File {
  preview?: string;
}

const Signup = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const formik = useFormik({
    initialValues: {
      email: '',
      phone: '',
      profilePicture: null as FileWithPreview | null,
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Email invalide').required('Email requis'),
      phone: Yup.string().matches(/^\+?[0-9\s-()]+$/, 'Numéro de téléphone invalide').required('Numéro de téléphone requis'),
      profilePicture: Yup.mixed()
        .required('Photo de profil requise')
        .test('fileType', 'Format de fichier non accepté', (value: any) => {
          if (!value) return true;
          return ['image/jpeg', 'image/png', 'image/jpg'].includes(value.type);
        })
        .test('fileSize', 'Fichier trop volumineux (max 5MB)', (value: any) => {
          if (!value) return true;
          return value.size <= 5 * 1024 * 1024;
        }),
      password: Yup.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères').required('Mot de passe requis'),
    }),
    onSubmit: (values) => {
      // Logique d'inscription
      console.log('Inscription avec', values);
      navigate('/login');
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files;
    if (files && files.length > 0) {
      const file = files[0];
      formik.setFieldValue('profilePicture', file);
      
      // Créer une URL pour la prévisualisation
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: '#000',
        pt: '64px', // Hauteur de la navbar pour éviter le chevauchement
        pb: '64px', // Espace pour le footer
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%', // Agrandir pour couvrir toute la largeur
          height: '150%', // Agrandir pour couvrir toute la hauteur
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(0,0,0,0) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2, py: 4 }}>
        <Box 
          sx={{ 
            maxWidth: 400, 
            mx: 'auto', 
            p: 3, 
            boxShadow: '0 0 30px rgba(255,255,255,0.2)',
            borderRadius: 2,
            backgroundColor: '#fff',
          }}
        >
          <Typography variant="h4" gutterBottom align="center">Inscription</Typography>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
            />
            <TextField
              fullWidth
              id="phone"
              name="phone"
              label="Numéro de téléphone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              margin="normal"
            />
            
            {/* Sélecteur de fichier pour la photo de profil */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body1" component="p" gutterBottom>
                Photo de profil
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                {previewImage && (
                  <Box sx={{ mb: 2, width: '100%', textAlign: 'center' }}>
                    <img 
                      src={previewImage} 
                      alt="Prévisualisation" 
                      style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }} 
                    />
                  </Box>
                )}
                
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  sx={{ mt: 1 }}
                >
                  {formik.values.profilePicture ? 'Changer de photo' : 'Sélectionner une photo'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
                
                {formik.touched.profilePicture && formik.errors.profilePicture && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formik.errors.profilePicture}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Mot de passe"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
            />
            <Button 
              color="primary" 
              variant="contained" 
              fullWidth 
              type="submit" 
              sx={{ mt: 3, mb: 1, py: 1.5 }}
            >
              S'inscrire
            </Button>
            
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              Déjà inscrit ? <Button color="primary" component={Link} to="/login" sx={{ p: 0, minWidth: 'auto' }}>Se connecter</Button>
            </Typography>
          </form>
        </Box>
      </Container>
    </Box>
  );
};

export default Signup;